import React, { createContext, useContext, useMemo } from 'react';

import type { IncidentItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import type { DateRange } from '@/types';

export interface FiltersState {
  incidentTypeID: IncidentItem['IncidentTypeID'][];
  severityLevel: IncidentItem['SeverityLevel'][];
  incidentLocations: IncidentItem['Address'][];
  dateRange: DateRange[];
  verificationSources: IncidentItem['VerificationSource'][];
  industries: IncidentItem['Industry'][];
}

export type FiltersFlags = {
  [K in keyof FiltersState]: boolean;
};

/**
 * Incident filters as consumed by the filters panel, independent of the page that hosts it.
 * `useIncidentFiltersSource` throws when no `IncidentFiltersSourceProvider` is mounted.
 */
export interface IncidentFiltersSource {
  filters: FiltersState;
  filterFlags?: FiltersFlags;
  applyFilters(next: Partial<FiltersState>): void;
  resetFilters(): void;
}

interface IncidentFiltersSourceProviderProps {
  filters: FiltersState;
  filterFlags?: FiltersFlags;
  onApply: IncidentFiltersSource['applyFilters'];
  onReset: IncidentFiltersSource['resetFilters'];
}

export const IncidentFiltersSourceContext = createContext<IncidentFiltersSource | undefined>(undefined);

/**
 * Takes typed props and builds the context value internally, so hosts don't repeat the `useMemo`
 * plumbing. Must not read page-specific contexts; the filters state arrives through props and is
 * the single source of truth that `IncidentsFeedSourceProvider` reads back.
 */
export const IncidentFiltersSourceProvider: React.FC<React.PropsWithChildren<IncidentFiltersSourceProviderProps>> = ({
  filters,
  filterFlags,
  onApply,
  onReset,
  children,
}) => {
  const value = useMemo<IncidentFiltersSource>(
    () => ({ filters, filterFlags, applyFilters: onApply, resetFilters: onReset }),
    [filters, filterFlags, onApply, onReset],
  );

  return React.createElement(IncidentFiltersSourceContext.Provider, { value }, children);
};

export const useIncidentFiltersSource = (): IncidentFiltersSource => {
  const context = useContext(IncidentFiltersSourceContext);

  if (!context) {
    throw new Error('useIncidentFiltersSource must be used within an IncidentFiltersSourceProvider');
  }

  return context;
};
